import * as amplitude from '@amplitude/analytics-browser';
import { sessionReplayPlugin } from '@amplitude/plugin-session-replay-browser';
import { identify, setupAnalytics } from '@cint/analytics';
import * as Sentry from '@sentry/react';
import jwtDecode from 'jwt-decode';
import { useCallback, useEffect } from 'react';

import { useAuth } from '@/auth';
import { PERMISSIONS_PREFIX } from '@/constants';
import { usePermissions } from '@/context/PermissionsContext';

const rootEnv = window.LUCID_SPA_CONFIG;

export const SENTRY_DSN = rootEnv?.SENTRY_DSN || '';
export const DEPLOYMENT_ENV = rootEnv?.DEPLOYMENT_ENV || '';
export const APP_VERSION = rootEnv?.APP_VERSION || '';
export const IS_TEST = import.meta.env.MODE === 'test';
export const AMPLITUDE_API_KEY = rootEnv?.AMPLITUDE_API_KEY || '';
export const PROD_DEPLOYMENT_ENV = 'prd';

const isDevOrTest = import.meta.env.DEV || IS_TEST;

export const Analytics = () => {
  const { user, getAccessToken } = useAuth();
  const { roles } = usePermissions();

  const getUser = useCallback(async () => {
    const token = await getAccessToken();
    const decodedToken = token && jwtDecode(token);
    const userIdFromToken = decodedToken?.['http://lucid/user_id'];

    const analyticsUser = {
      id: userIdFromToken || user?.sub || '',
      user_id: userIdFromToken || user?.sub || '',
      account_id: userIdFromToken || user?.sub || '',
      email: user?.email || '',
      las_role_name:
        roles.find((role) => role.startsWith(PERMISSIONS_PREFIX)) || '',
    };

    return analyticsUser;
  }, [getAccessToken, roles, user]);

  useEffect(() => {
    if (!isDevOrTest) {
      getUser().then((analyticsUser) => {
        identify(analyticsUser);
        amplitude.setUserId(analyticsUser.user_id);
      });
    }
  }, [getUser]);

  useEffect(() => {
    if (!isDevOrTest) {
      setupAnalytics({
        deploymentEnv: DEPLOYMENT_ENV,
        releaseVersion: APP_VERSION,
        sentryConfig: {
          dsn: SENTRY_DSN,
          replaysSessionSampleRate: 0.1,
          replaysOnErrorSampleRate: 1.0,
          // @cint/analytics bundles its own (older) @sentry/react, so its
          // Integration type doesn't structurally match this one at the
          // type level even though they're runtime-compatible.
          integrations: [
            Sentry.browserTracingIntegration(),
            ...(DEPLOYMENT_ENV === PROD_DEPLOYMENT_ENV
              ? [Sentry.replayIntegration()]
              : []),
          ] as any,
        },
        amplitudeConfig: {
          apiKey: AMPLITUDE_API_KEY,
          options: {
            appVersion: APP_VERSION,
          },
          plugins: [
            sessionReplayPlugin({
              sampleRate: 0.03,
            }),
          ],
        },
      });
    }
  }, []);

  return null;
};
